import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ShieldCheck, ShieldAlert, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLOR = {
  Bronze:   { ring: 'ring-amber-300',  text: 'text-amber-700',  bar: 'bg-amber-500' },
  Silver:   { ring: 'ring-slate-300',  text: 'text-slate-700',  bar: 'bg-slate-500' },
  Gold:     { ring: 'ring-yellow-300', text: 'text-yellow-700', bar: 'bg-yellow-500' },
  Platinum: { ring: 'ring-violet-300', text: 'text-violet-700', bar: 'bg-violet-500' },
};

/**
 * <TrustScoreCard>
 * Larger, breakdown-style widget for the Profile page. Shows the score, tier,
 * a progress ring, the verified state, and a list of point sources.
 */
export default function TrustScoreCard() {
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/users/me/trust`, { withCredentials: true });
        setTrust(res.data);
      } catch {
        setTrust(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!trust) return null;

  const tier = TIER_COLOR[trust.tier] || TIER_COLOR.Bronze;

  return (
    <Card className="mt-6 border-stone-200" data-testid="trust-score-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
            Trust Score
          </span>
          <span className={`text-sm font-bold ${tier.text}`} data-testid="trust-score-tier">
            {trust.tier}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6 mb-5">
          <div
            className={`relative w-20 h-20 rounded-full bg-stone-100 ring-4 ${tier.ring} flex items-center justify-center`}
            data-testid="trust-score-ring"
          >
            <div className={`text-3xl font-bold ${tier.text}`}>{trust.score}</div>
          </div>
          <div className="flex-1">
            <Progress value={trust.score} className="h-2 mb-2" />
            <div className="text-sm text-stone-600">
              {trust.score} / 100 — {trust.score >= 90 ? 'Top tier' : trust.score >= 70 ? 'Excellent' : trust.score >= 40 ? 'Good standing' : 'Building reputation'}
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm">
              {trust.is_bank_verified ? (
                <span className="inline-flex items-center text-emerald-700 font-semibold" data-testid="trust-verified-flag">
                  <ShieldCheck className="w-4 h-4 mr-1" /> Bank-verified via Plaid
                </span>
              ) : (
                <span className="inline-flex items-center text-amber-700">
                  <ShieldAlert className="w-4 h-4 mr-1" />
                  Not yet bank-verified — link a bank to unlock +30 points
                </span>
              )}
            </div>
          </div>
        </div>

        {trust.breakdown?.length > 0 && (
          <div className="space-y-2 pt-4 border-t border-stone-100">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
              Where your points come from
            </div>
            {trust.breakdown.map((b, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-sm bg-stone-50 rounded-lg px-3 py-2"
                data-testid={`trust-breakdown-${i}`}
              >
                <span className="text-stone-700">{b.label}</span>
                <span className="font-semibold text-emerald-600">+{b.points}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
